import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faSignOutAlt,
  faUser,
  faChevronDown,
  faChevronUp,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import theme from '../styles/theme';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.bgMesh};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  padding: 0;
  padding-bottom: 60px;
  padding-top: 80px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100% - ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px);
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 72px;
  }
`;

const TopBar = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 28px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.topbar};
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  z-index: 100;
  height: 64px;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    padding: 14px 20px;
  }
`;

const ToggleButton = styled.button`
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radius.sm};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: rgba(255, 107, 107, 0.06);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: ${theme.radius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.08);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.2;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.accentGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.25);
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${theme.colors.card};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.cardBorder};
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 107, 107, 0.06);
    color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

const PageContainer = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.xl};
  padding: 32px;
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.cardBorder};
  margin: 0 24px 32px 24px;
  width: calc(100% - 48px);

  @media (max-width: 768px) {
    padding: 24px 18px;
    margin: 0 16px 24px 16px;
    width: calc(100% - 32px);
  }
`;

const PageHeader = styled.div`
  margin-bottom: 28px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: ${theme.colors.text};
  margin: 0 0 8px 0;
  letter-spaciing: -0.03em;
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    background: ${theme.colors.accentGradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: ${theme.colors.textMuted};
  margin: 0 0 20px 0;
  line-height: 1.5;
  max-width: 640px;
`;

const SearchBar = styled.div`
  position: relative;
  max-width: 420px;
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 42px;
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.radius.pill};
  font-size: 14px;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.12);
    background: ${theme.colors.card};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AccordionItem = styled.div<{ $isOpen: boolean }>`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.md};
  border: 1px solid ${props => (props.$isOpen ? 'rgba(255, 107, 107, 0.35)' : theme.colors.cardBorder)};
  box-shadow: ${props => (props.$isOpen ? theme.shadows.sm : 'none')};
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  border-left: 4px solid ${props => (props.$isOpen ? theme.colors.primary : 'transparent')};
`;

const AccordionHeader = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  background: ${props => (props.$isOpen ? 'rgba(255, 107, 107, 0.04)' : 'transparent')};
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  font-size: 15px;
  font-weight: ${props => (props.$isOpen ? 700 : 600)};
  color: ${theme.colors.text};
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: rgba(255, 107, 107, 0.06);
  }
`;

const AccordionIcon = styled(FontAwesomeIcon)<{ $isOpen: boolean }>`
  font-size: 14px;
  color: ${props => (props.$isOpen ? theme.colors.primary : theme.colors.textMuted)};
  flex-shrink: 0;
`;

const AccordionPanel = styled.div`
  padding: 0 24px 20px 24px;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.65;
  background: ${theme.colors.card};
`;

const EmptySearch = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  padding: 32px 16px;
  font-size: 15px;
`;

const faqs = [
  {
    question: 'How long does it take to set up a WhatzPro account?',
    answer:
      'Setup takes less than 10 minutes. Connect your GoHighLevel location, complete subscription, scan the WhatsApp QR code, and you can start messaging right away.',
  },
  {
    question: 'Are there prerequisites before using WhatsApp with WhatzPro?',
    answer:
      'Avoid connecting a brand-new WhatsApp number immediately after registration. Warm up the number by sending and receiving messages from the phone app for 2–3 weeks before linking it to WhatzPro.',
  },
  {
    question: 'How many sub-accounts can I add during the trial?',
    answer:
      'During the 14-day trial on eligible plans, you can add one sub-account. After the trial, limits depend on your subscription plan (Single or 11+ Sub-accounts).',
  },
  {
    question: 'Are there restrictions during the trial period?',
    answer:
      'Some features may be limited while your subscription status is pending payment. Complete Razorpay checkout to unlock full access. Demo accounts follow separate rules.',
  },
  {
    question: 'Do I need WhatsApp Personal or Business?',
    answer:
      'You can connect either WhatsApp Personal or WhatsApp Business via QR code scan. Both work with WhatzPro for sending and receiving messages through GoHighLevel.',
  },
  {
    question: 'How do I disconnect WhatsApp from a location?',
    answer:
      'Go to Subscribed Locations, click Disconnect WhatsApp on the row for that location, and confirm. You can reconnect anytime by scanning the QR code again.',
  },
  {
    question: 'Where can I view and download my invoices?',
    answer:
      'Open Invoices from the sidebar to see your billing history. Use the date filter to narrow results, or download any invoice as a PDF.',
  },
];

const FAQ: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel,
  } = useLogoutHandler();

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => setIsMobileOpen(!isMobileOpen);

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton
            onClick={() => {
              if (window.innerWidth <= 768) {
                toggleMobileMenu();
              } else {
                setIsCollapsed(!isCollapsed);
              }
            }}
          >
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogoutClick}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContainer>
          <PageHeader>
            <PageTitle>
              <span>FAQ</span>
            </PageTitle>
            <PageSubtitle>
              Find answers about setup, WhatsApp connection, subscriptions, and billing.
            </PageSubtitle>
            <SearchBar>
              <SearchIcon>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
              <SearchInput
                type="search"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          </PageHeader>

          <Accordion>
            {filteredFaqs.length === 0 ? (
              <EmptySearch>No questions match your search.</EmptySearch>
            ) : (
              filteredFaqs.map((faq, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <AccordionItem key={faq.question} $isOpen={isOpen}>
                    <AccordionHeader
                      $isOpen={isOpen}
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      {faq.question}
                      <AccordionIcon
                        $isOpen={isOpen}
                        icon={isOpen ? faChevronUp : faChevronDown}
                      />
                    </AccordionHeader>
                    {isOpen && <AccordionPanel>{faq.answer}</AccordionPanel>}
                  </AccordionItem>
                );
              })
            )}
          </Accordion>
        </PageContainer>
      </MainContent>

      <Footer isCollapsed={isCollapsed} />

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default FAQ;
